import React from "react";
import { motion } from "framer-motion";
import "./ServiceArea.css";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import CertificationLogos from "../../components/CertificationLogos";
import { servicesIcons } from "./servicesIcons";

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const gridVariants = {
  visible: { transition: { staggerChildren: 0.07 } },
};

/**
 * One service area, laid out as a full-width block.
 *
 * The areas carry very different amounts of detail — six standards with
 * benefit lists in one, two named certifications in another — so each gets its
 * own band rather than a grid cell, and only the evenly sized standards inside
 * are rendered as cards.
 */
/* The whole card is the link when it has a page; otherwise a plain block. */
const CardLink = ({ to, className, children }) =>
  to ? (
    <Link to={to} className={`${className} rounded-[inherit] text-inherit hover:text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-orange)]`}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );

const ServiceArea = ({ area }) => {
  const Icon = servicesIcons[area.icon];
  const headingId = `service-area-${area.id}`;

  return (
    <article
      id={area.id}
      className="sectionlayout py-12 lg:py-16"
      aria-labelledby={headingId}
    >
      {/* No icon chip beside the area title — removed on the user's instruction
          (2026-09-14) for every area. The title carries itself at this size,
          with the brand rule under it. The area icon is still resolved above:
          it is the fallback inside cards whose standard has no logo file. */}
      {/* Centred on the user's instruction (2026-09-14) — every area heading,
          not just the ones with a page of their own, so the run of seven reads
          as one column down the page. */}
      <header className="text-center">
        {/* the title and, where an area has a page of its own, the link to it.
            `flex-wrap` so the link drops under the title on a narrow screen
            instead of squeezing it. */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
          <h3
            id={headingId}
            className="max-w-3xl text-2xl md:text-3xl font-bold leading-tight text-gray-900"
          >
            {area.title}
          </h3>
          {area.readMore && (
            <Link
              to={area.readMore.to}
              className="service-area__more"
              aria-label={`${area.readMore.label} — ${area.title}`}
            >
              <span>{area.readMore.label}</span>
              <FiArrowRight aria-hidden="true" className="service-area__more-arrow" />
            </Link>
          )}
        </div>
        <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-[var(--brand-orange)]" />
        {/* The sub-heading is centred with the title above it; the LIST LABEL
            under it is not — that line introduces the grid, so it sits to the
            side where the cards start (user's instruction, 2026-09-14). */}
        {area.intro && (
          <p className="mx-auto mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-gray-600">
            {area.intro}
          </p>
        )}
        {area.listLabel && (
          <p className="mt-6 text-left text-sm md:text-base font-semibold text-gray-900">
            {area.listLabel}
          </p>
        )}
      </header>

      {area.bullets && (
        <motion.ul
          className="mt-6 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={gridVariants}
        >
          {area.bullets.map((bullet) => (
            <motion.li
              key={bullet}
              variants={cardVariants}
              className="brand-card"
            >
              <CardLink
                to={area.bulletsTo}
                className="flex h-full items-center gap-3 px-5 py-4"
              >
                <FiCheckCircle
                  aria-hidden="true"
                  size={18}
                  className="flex-shrink-0 text-[var(--brand-primary)]"
                />
                <span className="text-sm md:text-base font-semibold text-gray-900">
                  {bullet}
                </span>
              </CardLink>
            </motion.li>
          ))}
        </motion.ul>
      )}

      {area.items && (
        <motion.ul
          className="mt-8 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={gridVariants}
        >
          {area.items.map((item) => (
            <motion.li
              key={item.title}
              variants={cardVariants}
              className="brand-card"
            >
              <CardLink to={item.to} className="block h-full p-6">
              {/* The standard's own mark leads the card. Where the project
                  holds no logo file for a standard the area's icon stands in —
                  a plainly decorative UI chip, never a stand-in certification
                  mark — so every card in a row starts at the same height. */}
              <CertificationLogos
                logoKeys={item.logoKeys}
                label={`${item.title} certification mark`}
                /* 4.5rem — the same illustration box PageIntro's cards use,
                   so artwork across the site reads at one weight */
                className="flex h-[4.5rem] items-center"
                size="h-[4.5rem]"
                fallback={
                  Icon ? (
                    <span className="flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-[var(--brand-primary)] text-white shadow-md">
                      <Icon aria-hidden="true" className="h-8 w-8" />
                    </span>
                  ) : null
                }
              />

              <h4 className="mt-4 text-base font-bold leading-snug text-gray-900">
                {item.title}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">
                {item.description}
              </p>

              {item.benefits && (
                <>
                  <p className="mt-4 text-sm font-semibold text-gray-900">
                    {item.benefitsLabel}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {item.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-2.5">
                        <FiCheckCircle
                          aria-hidden="true"
                          size={16}
                          className="mt-0.5 flex-shrink-0 text-[var(--brand-primary)]"
                        />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              </CardLink>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </article>
  );
};

export default ServiceArea;
